import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { AppointmentDialog } from "./AppointmentDialog";
import { QuickAppointmentDialog } from "./QuickAppointmentDialog";
import { cn } from "@/lib/utils";

interface Appointment {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  appointment_type: string;
  crm_contact_id: string;
  crm_contacts: { name: string };
}

export function AppointmentCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickDialogOpen, setQuickDialogOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>();

  useEffect(() => {
    fetchAppointments();
    
    const channel = supabase
      .channel('appointments-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments' },
        () => {
          fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentDate]);

  const fetchAppointments = async () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);

    const { data } = await supabase
      .from("appointments")
      .select("*, crm_contacts(name)")
      .gte("start_time", start.toISOString())
      .lte("start_time", end.toISOString())
      .order("start_time");

    if (data) setAppointments(data as Appointment[]);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getAppointmentsForDay = (day: Date) => {
    return appointments.filter((apt) =>
      isSameDay(parseISO(apt.start_time), day)
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "confirmed":
        return "bg-green-500/10 text-green-700 dark:text-green-300";
      case "cancelled":
        return "bg-red-500/10 text-red-700 dark:text-red-300";
      case "completed":
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
      default:
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300";
    }
  };

  const handleCreateAppointment = () => {
    setSelectedAppointmentId(undefined);
    setDialogOpen(true);
  };

  const handleQuickAppointment = () => {
    setQuickDialogOpen(true);
  };

  const handleEditAppointment = (id: string) => {
    setSelectedAppointmentId(id);
    setDialogOpen(true);
  };

  const firstDayOfWeek = monthStart.getDay();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">{format(currentDate, "MMMM yyyy")}</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleQuickAppointment}>
            <Plus className="h-4 w-4 mr-2" />
            Quick Appointment
          </Button>
          <Button onClick={handleCreateAppointment}>
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-7 gap-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center font-semibold text-sm text-muted-foreground py-2">
              {day}
            </div>
          ))}
          
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[120px] border rounded-lg bg-muted/20" />
          ))}
          
          {daysInMonth.map((day) => {
            const dayAppointments = getAppointmentsForDay(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "min-h-[120px] border rounded-lg p-2 space-y-1 cursor-pointer hover:bg-muted/50 transition-colors",
                  !isSameMonth(day, currentDate) && "opacity-50",
                  isToday && "border-primary"
                )}
                onClick={() => setSelectedDate(day)}
              >
                <div className={cn(
                  "text-sm font-medium",
                  isToday && "text-primary font-bold"
                )}>
                  {format(day, "d")}
                </div>
                <div className="space-y-1">
                  {dayAppointments.slice(0, 2).map((apt) => (
                    <div
                      key={apt.id}
                      className={cn(
                        "text-xs p-1 rounded cursor-pointer",
                        getStatusColor(apt.status)
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAppointment(apt.id);
                      }}
                    >
                      <div className="font-medium truncate">{format(parseISO(apt.start_time), "HH:mm")}</div>
                      <div className="truncate">{apt.title}</div>
                    </div>
                  ))}
                  {dayAppointments.length > 2 && (
                    <div className="text-xs text-muted-foreground">
                      +{dayAppointments.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {selectedDate && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">{format(selectedDate, "EEEE, MMMM d, yyyy")}</h3>
          <div className="space-y-2">
            {getAppointmentsForDay(selectedDate).length === 0 ? (
              <p className="text-muted-foreground text-sm">No appointments scheduled</p>
            ) : (
              getAppointmentsForDay(selectedDate).map((apt) => (
                <div
                  key={apt.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleEditAppointment(apt.id)}
                >
                  <div className="flex-1">
                    <div className="font-medium">{apt.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {format(parseISO(apt.start_time), "HH:mm")} - {format(parseISO(apt.end_time), "HH:mm")}
                      {apt.crm_contacts && ` • ${apt.crm_contacts.name}`}
                    </div>
                  </div>
                  <Badge className={getStatusColor(apt.status)}>{apt.status}</Badge>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        appointmentId={selectedAppointmentId}
        onSuccess={fetchAppointments}
      />

      <QuickAppointmentDialog
        open={quickDialogOpen}
        onOpenChange={setQuickDialogOpen}
        onSuccess={fetchAppointments}
      />
    </div>
  );
}
